import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { expensesService } from "./expenses.service";

export const fetchExpenses = createAsyncThunk(
    "expenses/fetchAll",
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await expensesService.getAll(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch expenses");
        }
    }
);

export const fetchCategories = createAsyncThunk(
    "expenses/fetchCategories",
    async (_, { rejectWithValue }) => {
        try {
            const response = await expensesService.getCategories();
            return response.categories;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch categories");
        }
    }
);

export const createExpense = createAsyncThunk(
    "expenses/create",
    async (expenseData, { rejectWithValue }) => {
        try {
            const response = await expensesService.create(expenseData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to create expense");
        }
    }
);

export const updateExpense = createAsyncThunk(
    "expenses/update",
    async ({ id, expenseData }, { rejectWithValue }) => {
        try {
            const response = await expensesService.update(id, expenseData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to update expense");
        }
    }
);

export const deleteExpense = createAsyncThunk(
    "expenses/delete",
    async (id, { rejectWithValue }) => {
        try {
            await expensesService.delete(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to delete expense");
        }
    }
);

const initialState = {
    expenses: [],
    categories: [],
    pagination: null,
    summary: null,
    isLoading: false,
    error: null,
};

const expensesSlice = createSlice({
    name: "expenses",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch expenses
            .addCase(fetchExpenses.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchExpenses.fulfilled, (state, action) => {
                state.isLoading = false;
                state.expenses = action.payload.data;
                state.pagination = action.payload.pagination;
                state.summary = action.payload.summary;
            })
            .addCase(fetchExpenses.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Fetch categories
            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.categories = action.payload;
            })
            // Create expense
            .addCase(createExpense.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createExpense.fulfilled, (state, action) => {
                state.isLoading = false;
                state.expenses.unshift(action.payload);
            })
            .addCase(createExpense.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Update expense
            .addCase(updateExpense.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateExpense.fulfilled, (state, action) => {
                state.isLoading = false;
                const index = state.expenses.findIndex((e) => e.id === action.payload.id);
                if (index !== -1) {
                    state.expenses[index] = action.payload;
                }
            })
            .addCase(updateExpense.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Delete expense
            .addCase(deleteExpense.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteExpense.fulfilled, (state, action) => {
                state.isLoading = false;
                state.expenses = state.expenses.filter((e) => e.id !== action.payload);
            })
            .addCase(deleteExpense.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearError } = expensesSlice.actions;
export default expensesSlice.reducer;
